import { useApolloClient } from '@apollo/client';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { json2csv } from 'json-2-csv';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import {
  CustomerDisplaysIndexPageDocument,
  CustomerDisplaysIndexPageQuery,
  CustomerDisplaysIndexPageQueryVariables,
} from '~pages/[organization]/customers/[handle]/displays/__generated__/Index.graphql';
import { CustomersGridItem_CustomerFragment } from './__generated__/CustomersGridItem.graphql';

interface Props {
  customer: CustomersGridItem_CustomerFragment;
  isOpen: boolean;
  onCancel: () => void;
}
type Display = NonNullable<
  NonNullable<CustomerDisplaysIndexPageQuery['customer']>['displays']
>[number];
type DisplayRow = Record<string, string | number | null | undefined>;

export function ExportCSVModal({ customer, isOpen, onCancel }: Props) {
  const pageSize = 50;
  const totalPages = Math.ceil((customer.displayCount || 0) / pageSize);

  const [isPreparing, setIsPreparing] = useState(false);
  const [preparedCount, setPreparedCount] = useState(0);
  const [allRows, setAllRows] = useState<DisplayRow[]>([]);
  const client = useApolloClient();
  const abortControllerRef = useRef<AbortController | null>(null);
  const handleCancel = () => {
    abortControllerRef.current?.abort();
    onCancel();
  };

  const [downloadError, setDownloadError] = useState(false);

  const prepareAllPages = useCallback(async () => {
    abortControllerRef.current?.abort(); // cancel any previous run
    abortControllerRef.current = new AbortController();
    setIsPreparing(true);
    setPreparedCount(0);
    setAllRows([]);
    const rows: DisplayRow[] = [];
    try {
      for (let page = 0; page < totalPages; page++) {
        const result = await client.query<
          CustomerDisplaysIndexPageQuery,
          CustomerDisplaysIndexPageQueryVariables
        >({
          query: CustomerDisplaysIndexPageDocument,
          variables: { customerHandle: customer.handle, pageSize, pageNumber: page },
          context: {
            fetchOptions: { signal: abortControllerRef.current.signal },
          },
        });
        const displays = result.data?.customer?.displays || [];
        displays.forEach((display) => {
          rows.push(mapDisplayToRow(display));
        });
        setPreparedCount(rows.length);
        setAllRows([...rows]);
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      throw err;
    } finally {
      setIsPreparing(false);
    }
  }, [client, customer.handle, totalPages]);

  useEffect(() => {
    if (isOpen) {
      prepareAllPages();
    }
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [isOpen, prepareAllPages]);

  const mapDisplayToRow = (display: Display): DisplayRow => {
    const row: DisplayRow = {
      Alias: display.alias ?? '',
      Site: display.site?.name ?? '',
      'Platform Name': display.platform.name,
      'Platform Type': display.platform.type,
      'Serial Number': display.serialNumber,
      'Wave Agent Version': display.agentVersion ?? '',
      'Firmware Version': display.firmware?.android.version ?? '',
      'Ethernet MAC': display.networkInformation.ethernetMacAddress,
      'Wi-Fi MAC': display.networkInformation.wifiMacAddress ?? '',
      'Local IP': display.networkInformation.localIPAddress ?? '',
      'Model Number': display.commercialTypeNumber,
    };

    display.groups.forEach((group, index) => {
      row[`Group ${index + 1}`] = group.name;
    });

    return row;
  };

  const handleDownload = async () => {
    try {
      const csvData = await json2csv(allRows, { emptyFieldValue: '' });
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', `${customer.name}-data.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      setDownloadError(true);
    }
  };
  return (
    <Modal isOpen={isOpen} onClose={onCancel} closeOnOverlayClick={false}>
      <ModalOverlay />

      <ModalContent>
        <ModalHeader>{t('downloadDisplayData')}</ModalHeader>
        <ModalCloseButton onClick={handleCancel} />
        <ModalBody>
          {downloadError
            ? t('downloadErr')
            : isPreparing
            ? t('preparingDisplaysProgress', {
                prepared: preparedCount,
                total: customer.displayCount,
              })
            : t('displayDataReady')}
        </ModalBody>
        <ModalFooter>
          <Button variant={'ghost'} colorScheme="blue" onClick={handleCancel}>
            {t('cancel')}
          </Button>
          <Button
            variant={'solid'}
            colorScheme="blue"
            marginLeft="3"
            disabled={isPreparing || downloadError}
            isLoading={isPreparing}
            onClick={handleDownload}
          >
            {t('download')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
